import { useDirection } from "@radix-ui/react-direction";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type React from "react";

export interface Feature {
  icon?: React.ReactNode;
  description: string;
  /** Muted qualifier shown under the description, e.g. which card version offers it. */
  note?: string;
}

export interface OnboardingDataItem {
  title: string;
  description?: string[];
  footer?: string | null;
  features?: Feature[];
  imageSrc: string | (() => React.ReactElement);
  /**
   * Where the image sits: above the title by default, or under the title and
   * description when the title should be read first.
   */
  imagePosition?: "top" | "belowHeader";
}

interface Props {
  data: OnboardingDataItem[];
  finishLabel: string;
  onFinish: () => void;
}

export const OnboardingComponent = ({ data, finishLabel, onFinish }: Props) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const isLastStep = activeStep === data.length - 1;
  const direction = useDirection();
  const handleNext = () => {
    if (isLastStep) {
      onFinish();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderImageSrc = (imageSrc: string | (() => React.ReactElement)) => {
    if (typeof imageSrc === "string") {
      return (
        <img
          src={imageSrc}
          alt={data[activeStep].title}
          className="w-full h-48 object-contain bg-neutral-300 py-4"
        />
      );
    } else {
      return imageSrc();
    }
  };

  const isImageBelowHeader = data[activeStep].imagePosition === "belowHeader";

  return (
    <Card className="h-full flex flex-col">
      {!isImageBelowHeader && (
        <div className="pb-4">{renderImageSrc(data[activeStep].imageSrc)}</div>
      )}
      <CardHeader>
        {data.length > 1 && (
          <span className="text-xs mb-2 text-muted-foreground">
            {t("onboarding_step", {
              currentStep: activeStep + 1,
              totalSteps: data.length,
            })}
          </span>
        )}

        <CardTitle>
          <h5 className="text-2xl mb-2">{data[activeStep].title}</h5>
        </CardTitle>
        {data[activeStep].description && (
          <CardDescription className="space-y-2">
            {data[activeStep].description?.map((description, index) => (
              <p key={index}>{description}</p>
            ))}
          </CardDescription>
        )}
      </CardHeader>

      {isImageBelowHeader && (
        <div className="px-6 pb-6">
          {renderImageSrc(data[activeStep].imageSrc)}
        </div>
      )}

      <CardContent className="w-full flex-1">
        {data[activeStep].features && (
          <ul>
            {data[activeStep].features?.map((feature, index) => (
              <li className="flex py-2 items-center gap-x-4" key={index}>
                {feature.icon && (
                  <div className="min-w-14 shrink-0 text-muted-foreground">
                    {feature.icon}
                  </div>
                )}
                <div className="flex-1 basis-auto min-w-0">
                  <div>{`${
                    feature.icon ? "" : index + 1 + ". "
                  }${feature.description}`}</div>
                  {feature.note && (
                    <p className="text-sm text-muted-foreground">
                      {feature.note}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {data[activeStep].footer && (
          <p className="text-sm mt-4">{data[activeStep].footer}</p>
        )}
      </CardContent>
      <CardFooter
        className={cn("p-4 w-full", data.length > 1 ? "border-t" : "")}
      >
        {data.length > 1 ? (
          <div className="flex flex-row justify-between w-full">
            <Button
              variant="ghost"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              {direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
              {t("back")}
            </Button>
            <Button variant="ghost" onClick={handleNext}>
              {isLastStep ? finishLabel : t("Next")}
              {direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
            </Button>
          </div>
        ) : (
          <Button className="w-full" size="lg" onClick={handleNext}>
            {finishLabel}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
